/**
 * desktopTransitionStore — signals a pending "New Desktop" navigation.
 *
 * Phase flow: idle → pending → idle
 *
 * - idle:    no transition queued
 * - pending: a new desktop has been requested; DesktopTransitionLayer consumes
 *            the signal, tears down the old space store, and navigates to the
 *            new desktop. The incoming DesktopShell animates itself in via the
 *            `slideIn` route-state flag — there is no masking overlay.
 *
 * This indirection exists so non-React call sites (e.g. the AI's desktop_create
 * tool) can request the navigation without a router hook.
 */

import { create } from 'zustand';
import type { UISpace } from '../types/spaces';

export type DesktopTransitionPhase = 'idle' | 'pending';

interface DesktopTransitionState {
  phase: DesktopTransitionPhase;
  incomingSpaceId: string | null;
  incomingThreadId: string | null;
  incomingTabId: string | null;
  cachedSpace: UISpace | null;

  startTransition: (spaceId: string, threadId: string, tabId: string, cachedSpace: UISpace) => void;
  reset: () => void;
}

/**
 * Builds the `navigate()` args for a new-desktop transition from a set of
 * minted `DesktopLaunchIds`. Shared by `DesktopTransitionLayer` (the
 * non-React command path) and any React call site that navigates directly.
 *
 * The new desktop opens with chat in its normal docked state, same as any
 * other desktop — this does NOT trigger `desktopChatStore`'s centered
 * "intake" presentation (that remains reserved for first-run signup
 * onboarding, started separately by `Space.tsx`).
 */
export function buildDesktopLaunchNavArgs(
  spaceId: string,
  threadId: string,
  tabId: string,
  cachedSpace: UISpace,
) {
  return {
    to: `/desktops/${spaceId}`,
    options: {
      replace: false,
      state: {
        isPendingSpace: true,
        slideIn: true,
        cachedSpace,
        threadId,
        tabId,
      },
    },
  };
}

export const useDesktopTransitionStore = create<DesktopTransitionState>((set) => ({
  phase: 'idle',
  incomingSpaceId: null,
  incomingThreadId: null,
  incomingTabId: null,
  cachedSpace: null,

  startTransition: (spaceId, threadId, tabId, cachedSpace) =>
    set({ phase: 'pending', incomingSpaceId: spaceId, incomingThreadId: threadId, incomingTabId: tabId, cachedSpace }),

  reset: () => set({
    phase: 'idle',
    incomingSpaceId: null,
    incomingThreadId: null,
    incomingTabId: null,
    cachedSpace: null,
  }),
}));
